import { App, ModalFuncProps } from 'antd';
import React, { useEffect, useRef } from 'react';
import { usePrerenderUser } from '../../hooks/usePrerenderUser';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import store from '../../store';
import config from '../../assets/config';

const FORM_CONTAINER_ID = 'hubspot-form-container';

type HubspotFormInstance = Record<string, never>;
declare global {
  interface Window {
    hbspt?: {
      forms: {
        create: (config: HubSpotFormConfig) => HubspotFormInstance;
      };
    };
    _hsq?: any[];
  }
}

interface HubSpotFormConfig {
  portalId: string;
  formId: string;
  region: string;
  target: string;
  onFormReady?: (form: HTMLFormElement) => void;
}

interface HubSpotFormModalProps {
  onClose: () => void;
  userEmail?: string;
}

const initHubSpotForm = (): void => {
  if (!document.querySelector('script[src*="hsforms.net"]')) {
    const script = document.createElement('script');
    script.crossOrigin = 'anonymous';
    script.type = 'text/javascript';
    script.src = '//js-eu1.hsforms.net/forms/embed/v2.js';
    document.head.appendChild(script);
  }
};

const initHubSpotChat = (onLoad?: () => void): void => {
  if (!document.querySelector('script[src*="hs-scripts.com"]')) {
    const script = document.createElement('script');
    script.id = 'hs-script-loader';
    script.async = true;
    script.defer = true;    
    script.src = `//js-eu1.hs-scripts.com/${config.hubspotPortalId}.js`;
    script.onload = () => {
      // Initialize HubSpot's global queue if it doesn't exist
      // This queue is used for tracking and user identification
      window._hsq = window._hsq || [];
      onLoad?.();
    };
    script.onerror = (error) => {
      console.error('Error loading HubSpot script:', error);
    };
    document.head.appendChild(script);
  } else {
    window._hsq = window._hsq || [];
  }
};

const HubSpotFormModal: React.FC<HubSpotFormModalProps> = ({ onClose, userEmail }) => {
  const formInstanceRef = useRef<HubspotFormInstance>();
  const hubspotFormContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.hbspt && hubspotFormContainerRef.current) {
      if (window._hsq && userEmail) {
        window._hsq.push([
          'identify',
          {
            email: userEmail,
          },
        ]);
      }

      formInstanceRef.current = window.hbspt.forms.create({
        portalId: config.hubspotPortalId,
        formId: '2ac77811-8863-41c7-8f4f-df24d8b8a804',
        region: 'eu1',
        target: `#${FORM_CONTAINER_ID}`,
        onFormReady: (form) => {
          const emailInput = form.querySelector('input[name="email"]') as HTMLInputElement;
          if (emailInput && userEmail) {
            emailInput.value = userEmail;
          }
        },
      });
    } else {
      console.error('HubSpot script not loaded or container not found');
    }

    return () => {
      if (formInstanceRef.current) {
        if (hubspotFormContainerRef.current) {
          hubspotFormContainerRef.current.innerHTML = '';
        }
      }
    };
  }, [onClose, userEmail]);

  return <div id={FORM_CONTAINER_ID} ref={hubspotFormContainerRef} />;
};

const HubSpotFormWrapper: React.FC = () => {
  const { modal } = App.useApp();
  const user = usePrerenderUser();
  const modalInstanceRef = useRef<{ destroy: () => void } | null>(null);

  useEffect(() => {
    const handleClose = (): void => {
      if (modalInstanceRef.current) {
        modalInstanceRef.current.destroy();
      }
    };

    const modalConfig: ModalFuncProps = {
      content: <HubSpotFormModal onClose={handleClose} userEmail={user.email} />,
      title: 'Contact Support',
      footer: null,
      icon: null,
      width: 500,
      centered: true,
      closable: true,
      maskClosable: true,
      onCancel: handleClose,
    };

    modalInstanceRef.current = modal.confirm(modalConfig);

    return () => {
      if (modalInstanceRef.current) {
        modalInstanceRef.current.destroy();
      }
    };
  }, [user.email]);

  return null;
};

export const openHubSpotForm = (): void => {
  const container = document.createElement('div');
  document.body.appendChild(container);
  const root = ReactDOM.createRoot(container);
  root.render(
    <Provider store={store}>
      <App>
        <HubSpotFormWrapper />
      </App>
    </Provider>
  );
};

export const useHubSpot = (shouldLoad: boolean): void => {
  const user = usePrerenderUser();

  useEffect(() => {
    if (!shouldLoad || !user.hasSession) {
      return;
    }

    initHubSpotForm();

    const identifyUser = () => {
      if (window._hsq) {
        try {
          // Push user identification to HubSpot's queue
          // This will be processed by HubSpot when it's ready
          window._hsq.push([
            'identify',
            {
              id: String(user.id),
              email: user.email,
            },
          ]);
        } catch (error) {
          console.error('Error identifying user in HubSpot:', error);
        }
      } else {
        console.error('HubSpot not available for user identification');
      }
    };

    initHubSpotChat(identifyUser);
  }, [shouldLoad, user]);
};

function createFormUrl(firstName: string, email: string) {
  const parsedEmail = String(email).trim().toLowerCase();
  const parsedFirstName = String(firstName).trim();

  return `https://share-eu1.hsforms.com/1OkmQ6xvFR6mgyIfmKX-niAfc221?email=${encodeURIComponent(
    parsedEmail
  )}&firstname=${encodeURIComponent(parsedFirstName)}`;
}

export const HubSpot = {
  openHubSpotForm,
  useHubSpot,
  createFormUrl,
};
